import { useMemo } from 'react';
import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conciergeAPI, IRecentChat, IRecentChatsPage } from 'api/concierge';

export const RECENT_CHATS_QUERY_KEY = ['conciergeRecentChats'];
const PAGE_SIZE = 50;

export function useRecentChats(search: string) {
    const q = search.trim();
    const query = useInfiniteQuery({
        queryKey: [...RECENT_CHATS_QUERY_KEY, q],
        queryFn: ({ pageParam }) =>
            conciergeAPI.getRecentChats({ q: q || undefined, limit: PAGE_SIZE, offset: pageParam }),
        initialPageParam: 0,
        getNextPageParam: (last: IRecentChatsPage) => (last.has_more ? last.offset + last.data.length : undefined),
    });
    const chats = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

    return { ...query, chats };
}

type RecentChatsCache = InfiniteData<IRecentChatsPage, number>;

export function useRecentChatMutations() {
    const queryClient = useQueryClient();
    // A rename or pin answers with the updated row; it is laid over every cached list at once so the row
    // changes in place, then the lists refetch for the server's order (pinned first, newest activity).
    const applyRow = (row: IRecentChat) => {
        queryClient.setQueriesData<RecentChatsCache>({ queryKey: RECENT_CHATS_QUERY_KEY }, (cache) =>
            cache
                ? {
                      ...cache,
                      pages: cache.pages.map((page) => ({
                          ...page,
                          data: page.data.map((chat) => (chat.conversation_ts === row.conversation_ts ? row : chat)),
                      })),
                  }
                : cache,
        );
        return queryClient.invalidateQueries({ queryKey: RECENT_CHATS_QUERY_KEY });
    };

    const rename = useMutation({
        mutationFn: ({ conversationTs, title }: { conversationTs: string; title: string }) =>
            conciergeAPI.renameChat(conversationTs, title),
        onSuccess: applyRow,
    });
    const pin = useMutation({
        mutationFn: ({ conversationTs, pinned }: { conversationTs: string; pinned: boolean }) =>
            conciergeAPI.pinChat(conversationTs, pinned),
        onSuccess: applyRow,
    });
    const remove = useMutation({
        mutationFn: (conversationTs: string) => conciergeAPI.deleteChat(conversationTs),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: RECENT_CHATS_QUERY_KEY }),
    });

    return { rename, pin, remove };
}
